import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { Sms as SmsIcon } from '@mui/icons-material';
import { useAuth } from '../services/AuthService';
import { usePostHog } from '@posthog/react';
import { getSafeRedirectUrl } from '../utils/redirectUtils';
import {
  buildForgotPasswordUrl,
  buildLoginUrl,
  clearVerifyFlowContext,
  formatPhoneNumber,
  getVerifyFlowContext,
  resolvePostAuthDestination,
} from '../utils/authFlowUtils';

const RESEND_COOLDOWN_SECONDS = 60;

const VerifyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const phoneFromParams = searchParams.get('phone') || '';
  const returnTo = getSafeRedirectUrl(searchParams.get('returnTo'));
  const next = searchParams.get('next') === 'forgot-password' ? 'forgot-password' : 'login';
  const isRecoveryFlow = searchParams.has('next');
  const { confirmSignUp, autoSignIn, resendSignUpCode } = useAuth();
  const posthog = usePostHog();

  const [phone, setPhone] = useState(phoneFromParams);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRef = useRef(null);
  const autoResendAttemptedRef = useRef(false);
  const flowType = isRecoveryFlow ? next : 'signup';
  const currentFormattedPhone = formatPhoneNumber(phoneFromParams || phone);
  const hasTrustedResendContext = Boolean(getVerifyFlowContext({
    phone: currentFormattedPhone,
    next: flowType,
  }));
  const securityResendMessage = isRecoveryFlow
    ? 'For security, requesting a new verification code is only available right after Sign In or Forgot password in this browser.'
    : 'For security, requesting a new verification code is only available right after sign up in this browser.';

  // Auto-focus code input
  useEffect(() => {
    if (codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (!isRecoveryFlow || autoResendAttemptedRef.current) {
      return;
    }

    const formattedPhone = currentFormattedPhone;
    if (!formattedPhone) {
      return;
    }

    autoResendAttemptedRef.current = true;
    if (!hasTrustedResendContext) {
      return;
    }

    const now = Date.now();
    const storageKey = `labourando:verify:auto-resend:${next}:${formattedPhone}`;
    let lastAttemptAt = null;

    try {
      const storedValue = window.sessionStorage.getItem(storageKey);
      const parsed = Number.parseInt(storedValue || '', 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        lastAttemptAt = parsed;
      }
    } catch {
      // Ignore storage access failures and continue with in-memory protections.
    }

    if (lastAttemptAt) {
      const elapsedMs = now - lastAttemptAt;
      if (elapsedMs < RESEND_COOLDOWN_SECONDS * 1000) {
        const remainingSeconds = Math.max(
          Math.ceil((RESEND_COOLDOWN_SECONDS * 1000 - elapsedMs) / 1000),
          1
        );
        setResendCooldown(remainingSeconds);
        setSuccess('A verification code was sent recently. If it does not arrive, you can request another one after the cooldown.');
        return;
      }
    }

    let cancelled = false;

    const sendRecoveryCode = async () => {
      setResendLoading(true);
      setError('');
      setSuccess('');

      try {
        try {
          window.sessionStorage.setItem(storageKey, String(now));
        } catch {
          // Ignore storage access failures.
        }

        await resendSignUpCode(formattedPhone);

        if (cancelled) {
          return;
        }

        setPhone(formattedPhone);
        setSuccess('A new verification code has been sent to your phone.');
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } catch (err) {
        if (cancelled) {
          return;
        }

        try {
          window.sessionStorage.removeItem(storageKey);
        } catch {
          // Ignore storage access failures.
        }

        if (err.name === 'LimitExceededException') {
          setError('Too many attempts. Please wait a moment before trying again.');
          setResendCooldown(RESEND_COOLDOWN_SECONDS);
        } else if (err.name === 'InvalidParameterException') {
          setError('We could not send a new verification code. If your phone is already verified, sign in or reset your password.');
        } else {
          setError(err.message || 'Failed to send a new verification code. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setResendLoading(false);
        }
      }
    };

    sendRecoveryCode();

    return () => {
      cancelled = true;
    };
  }, [currentFormattedPhone, flowType, hasTrustedResendContext, isRecoveryFlow, next, resendSignUpCode]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const formattedPhone = formatPhoneNumber(phone);

    if (!formattedPhone) {
      setError('Phone number is required');
      return;
    }
    if (!code || code.length < 4) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    try {
      const result = await confirmSignUp(formattedPhone, code);
      if (result.success) {
        clearVerifyFlowContext();
        posthog?.capture('phone_verified');

        try {
          const autoSignInResult = await autoSignIn();

          if (autoSignInResult.success) {
            const destination = await resolvePostAuthDestination(returnTo);
            navigate(destination, { replace: true });
            return;
          }
        } catch (autoSignInError) {
          // Expected when the user returns later and the original sign-up session is gone.
          console.warn('Auto sign-in unavailable after verification:', autoSignInError);
        }

        if (next === 'forgot-password') {
          navigate(buildForgotPasswordUrl({ phone: formattedPhone, verified: true }), { replace: true });
          return;
        }

        navigate(buildLoginUrl({ phone: formattedPhone, returnTo, verified: true }), { replace: true });
      }
    } catch (err) {
      if (err.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please check and try again.');
      } else if (err.name === 'ExpiredCodeException') {
        setError('Verification code has expired. Please request a new one.');
      } else if (err.name === 'AliasExistsException') {
        setError('This phone number is already verified. Sign in to continue or reset your password if needed.');
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const formattedPhone = formatPhoneNumber(phone);

    if (resendCooldown > 0 || !formattedPhone) return;
    if (!getVerifyFlowContext({
      phone: formattedPhone,
      next: flowType,
    })) {
      setError(securityResendMessage);
      return;
    }

    setResendLoading(true);
    setError('');
    try {
      await resendSignUpCode(formattedPhone);
      setSuccess('A new verification code has been sent to your phone.');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      if (err.name === 'LimitExceededException') {
        setError('Too many attempts. Please wait a moment before trying again.');
      } else if (err.name === 'InvalidParameterException') {
        setError('We could not resend the code. If your phone is already verified, sign in or reset your password.');
      } else {
        setError(err.message || 'Failed to resend code. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <SmsIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Verify Your Phone
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            We sent a verification code to{' '}
            <strong>{phone || 'your phone'}</strong>
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {isRecoveryFlow && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {next === 'forgot-password'
              ? 'Verify your phone first, then you can reset your password.'
              : 'This account still needs phone verification before you can sign in. Enter the SMS code or resend a new one.'}
          </Alert>
        )}

        {!hasTrustedResendContext && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {securityResendMessage} If you already have the SMS code, you can still verify this account here.
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleVerify} noValidate>
          {!phoneFromParams && (
            <TextField
              fullWidth
              label="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              sx={{ mb: 2.5 }}
              autoComplete="tel"
            />
          )}

          <TextField
            fullWidth
            label="Verification Code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            sx={{ mb: 3 }}
            placeholder="Enter 6-digit code"
            inputRef={codeInputRef}
            slotProps={{
              input: {
                sx: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  letterSpacing: '0.5rem',
                },
              },
              htmlInput: {
                maxLength: 6,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: { textAlign: 'center' },
              },
            }}
            autoComplete="one-time-code"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !code || code.length < 4}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              py: 1.5,
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              onClick={handleResend}
              disabled={resendCooldown > 0 || resendLoading || !phone || !hasTrustedResendContext}
              sx={{ color: 'primary.main', textTransform: 'none' }}
            >
              {resendLoading ? (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              ) : null}
              {resendCooldown > 0
                ? `Resend code in ${resendCooldown}s`
                : 'Resend verification code'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link
              component={RouterLink}
              to={buildLoginUrl({ phone: formatPhoneNumber(phone), returnTo })}
              variant="body2"
              sx={{ color: 'primary.main' }}
            >
              Back to Sign In
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default VerifyPage;
